/**
 * LibraryManager - Component library system for OptimalAvailability
 * Provides library management, component storage, import/export, and built-in libraries
 * Matching AWB 4.0's .awbx library capability
 *
 * @module libraryManager
 * @requires none (in-memory storage with JSON serialization)
 */

class LibraryManager {
  constructor() {
    /** @type {Map<string, Object>} Library storage: libraryId -> library object */
    this.libraries = new Map();

    /** @type {number} Counter for generating unique library IDs */
    this._libraryCounter = 0;

    /** @type {number} Counter for generating unique component IDs */
    this._componentCounter = 0;

    // Initialize built-in libraries
    this._initializeBuiltInLibraries();
  }

  // ==================== OAV-076 (final): PLATFORM PERSISTENCE ====================

  /**
   * Hydrate this.libraries from the platform library service.
   * Returns the count loaded. Tolerates network failure (drops silently).
   * Wires libraryManager into /api/v1/platform/library/user/* endpoints.
   */
  async syncFromPlatform() {
    try {
      const fetcher = (typeof window !== 'undefined' && window.oavFetch) || fetch;
      const list = await fetcher('/api/v1/platform/library/user/list')
                   .then(r => (r && r.json) ? r.json() : r);
      if (!list || !Array.isArray(list.libraries)) return 0;
      for (const meta of list.libraries) {
        if (!this.libraries.has(meta.id)) {
          this.libraries.set(meta.id, { ...meta, components: [], hydrated: false });
        }
      }
      return list.libraries.length;
    } catch (e) {
      console.warn('[LibraryManager] syncFromPlatform failed:', e);
      return 0;
    }
  }

  /**
   * Persist a library to the platform service. Returns the server-assigned id.
   */
  async persistLibrary(library) {
    if (!library || !library.name) throw new Error('persistLibrary: library.name is required');
    const fetcher = (typeof window !== 'undefined' && window.oavFetch) || fetch;
    const res = await fetcher('/api/v1/platform/library/user/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: library.name, kind: 'failure-modes', payload: library }),
    });
    const data = (res && res.json) ? await res.json() : res;
    if (!data || !data.library_id) throw new Error('persistLibrary: no library_id returned');
    library.id = data.library_id;
    this.libraries.set(library.id, library);
    return library.id;
  }

  /**
   * Fetch the canonical platform library for a given equipment type.
   * The shared 768-equipment-type failure library is served from this endpoint
   * (OAV-050 cross-app library).
   */
  async loadFromPlatform(equipmentType) {
    const fetcher = (typeof window !== 'undefined' && window.oavFetch) || fetch;
    const slug = encodeURIComponent(equipmentType);
    const res = await fetcher(`/api/v1/platform/library/${slug}`);
    return (res && res.json) ? res.json() : res;
  }


  // ==================== LIBRARY MANAGEMENT ====================

  /**
   * Create a new library
   * @param {string} name - Library name
   * @param {string} description - Library description
   * @param {string} author - Library author
   * @returns {Object} Created library object { id, name, description, author, created, modified, components: [] }
   */
  createLibrary(name, description, author) {
    const libraryId = `lib_${++this._libraryCounter}_${Date.now()}`;

    const library = {
      id: libraryId,
      name: name,
      description: description,
      author: author,
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      components: [],
      version: '1.0.0',
      builtIn: false
    };

    this.libraries.set(libraryId, library);
    return library;
  }

  /**
   * Get all libraries
   * @param {Object} options - Options { includeBuiltIn: boolean }
   * @returns {Array<Object>} Array of library objects (excluding component data details)
   */
  getLibraries(options = {}) {
    const includeBuiltIn = options.includeBuiltIn !== false;

    return Array.from(this.libraries.values())
      .filter(lib => includeBuiltIn || !lib.builtIn)
      .map(lib => ({
        id: lib.id,
        name: lib.name,
        description: lib.description,
        author: lib.author,
        created: lib.created,
        modified: lib.modified,
        componentCount: lib.components.length,
        builtIn: lib.builtIn,
        version: lib.version
      }));
  }

  /**
   * Get a specific library with full details
   * @param {string} libraryId - Library ID
   * @returns {Object|null} Full library object or null if not found
   */
  getLibrary(libraryId) {
    return this.libraries.get(libraryId) || null;
  }

  /**
   * Delete a library
   * @param {string} libraryId - Library ID to delete
   * @returns {boolean} True if deleted, false if not found or built-in
   */
  deleteLibrary(libraryId) {
    const library = this.libraries.get(libraryId);

    if (!library) {
      return false;
    }

    if (library.builtIn) {
      throw new Error('Cannot delete built-in libraries');
    }

    this.libraries.delete(libraryId);
    return true;
  }

  // ==================== COMPONENT MANAGEMENT ====================

  /**
   * Add a component to a library
   * @param {string} libraryId - Library ID
   * @param {Object} component - Component object
   * @param {string} component.type - Component type: 'equipment'|'spare'|'labor'|'failureModel'|'weibullSet'|'bom'|'taskGroup'
   * @param {Object} component.data - Component data (varies by type)
   * @param {string} component.name - Component name
   * @param {string} component.description - Component description
   * @returns {Object} Created component with ID
   */
  addComponent(libraryId, component) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    const validTypes = ['equipment', 'spare', 'labor', 'failureModel', 'weibullSet', 'bom', 'taskGroup'];
    if (!validTypes.includes(component.type)) {
      throw new Error(`Invalid component type: ${component.type}`);
    }

    const componentId = `comp_${++this._componentCounter}_${Date.now()}`;

    const newComponent = {
      id: componentId,
      type: component.type,
      name: component.name,
      description: component.description || '',
      data: JSON.parse(JSON.stringify(component.data)), // Deep copy
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      tags: component.tags || []
    };

    library.components.push(newComponent);
    library.modified = new Date().toISOString();

    return newComponent;
  }

  /**
   * Remove a component from a library
   * @param {string} libraryId - Library ID
   * @param {string} componentId - Component ID to remove
   * @returns {boolean} True if removed, false if not found
   */
  removeComponent(libraryId, componentId) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    const initialLength = library.components.length;
    library.components = library.components.filter(c => c.id !== componentId);
    const removed = library.components.length < initialLength;

    if (removed) {
      library.modified = new Date().toISOString();
    }

    return removed;
  }

  /**
   * Get components from a library with optional filtering by type
   * @param {string} libraryId - Library ID
   * @param {string} filterType - Optional component type filter
   * @returns {Array<Object>} Array of component objects
   */
  getComponents(libraryId, filterType = null) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    let components = library.components;

    if (filterType) {
      components = components.filter(c => c.type === filterType);
    }

    return components.map(c => ({
      ...c,
      data: JSON.parse(JSON.stringify(c.data)) // Deep copy
    }));
  }

  /**
   * Search components across all libraries
   * @param {string} query - Search query (searches name, description, tags)
   * @returns {Array<Object>} Matching components with library info
   */
  searchComponents(query) {
    const results = [];
    const lowerQuery = query.toLowerCase();

    this.libraries.forEach(library => {
      library.components.forEach(component => {
        const matchesName = component.name.toLowerCase().includes(lowerQuery);
        const matchesDesc = component.description.toLowerCase().includes(lowerQuery);
        const matchesTags = component.tags.some(tag => tag.toLowerCase().includes(lowerQuery));

        if (matchesName || matchesDesc || matchesTags) {
          results.push({
            ...component,
            libraryId: library.id,
            libraryName: library.name,
            data: JSON.parse(JSON.stringify(component.data)) // Deep copy
          });
        }
      });
    });

    return results;
  }

  // ==================== IMPORT/EXPORT (.awbx format) ====================

  /**
   * Export a library to JSON string (our .awbx equivalent)
   * @param {string} libraryId - Library ID to export
   * @returns {string} JSON string representation of library
   */
  exportLibrary(libraryId) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    const exportData = {
      format: 'awbx-json',
      version: '1.0',
      library: JSON.parse(JSON.stringify(library)) // Deep copy
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Import a library from JSON content
   * @param {string} content - JSON string containing library data
   * @param {string} filename - Source filename (for reference)
   * @returns {Object} Imported library object
   */
  importLibrary(content, filename) {
    let data;

    try {
      data = JSON.parse(content);
    } catch (e) {
      throw new Error(`Invalid JSON content: ${e.message}`);
    }

    if (data.format !== 'awbx-json') {
      throw new Error('Invalid library format - expected awbx-json');
    }

    const library = data.library;

    // Validate library structure
    if (!library.name || !library.description) {
      throw new Error('Library missing required fields: name, description');
    }

    // Create new library with imported data
    const importedLibrary = {
      id: `lib_${++this._libraryCounter}_${Date.now()}`,
      name: `${library.name} (imported from ${filename})`,
      description: library.description,
      author: library.author || 'imported',
      created: library.created || new Date().toISOString(),
      modified: new Date().toISOString(),
      components: (library.components || []).map(comp => ({
        ...comp,
        id: `comp_${++this._componentCounter}_${Date.now()}` // Generate new IDs
      })),
      version: library.version || '1.0.0',
      builtIn: false,
      importedFrom: filename,
      importedDate: new Date().toISOString()
    };

    this.libraries.set(importedLibrary.id, importedLibrary);
    return importedLibrary;
  }

  /**
   * Export a single component
   * @param {string} libraryId - Library ID
   * @param {string} componentId - Component ID to export
   * @returns {string} JSON string representation of component
   */
  exportComponent(libraryId, componentId) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    const component = library.components.find(c => c.id === componentId);
    if (!component) {
      throw new Error(`Component not found: ${componentId}`);
    }

    const exportData = {
      format: 'awbx-component-json',
      version: '1.0',
      component: JSON.parse(JSON.stringify(component)), // Deep copy
      librarySource: {
        id: library.id,
        name: library.name
      }
    };

    return JSON.stringify(exportData, null, 2);
  }

  // ==================== APPLY TO PROJECT ====================

  /**
   * Apply a component to a project by merging its data
   * @param {string} libraryId - Library ID
   * @param {string} componentId - Component ID to apply
   * @param {Object} projectData - Current project data to merge into
   * @returns {Object} Updated project data with component merged
   */
  applyComponentToProject(libraryId, componentId, projectData) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    const component = library.components.find(c => c.id === componentId);
    if (!component) {
      throw new Error(`Component not found: ${componentId}`);
    }

    const updatedProject = JSON.parse(JSON.stringify(projectData)); // Deep copy

    // Merge component data based on type
    switch (component.type) {
      case 'equipment':
        if (!updatedProject.equipment) updatedProject.equipment = [];
        updatedProject.equipment.push(JSON.parse(JSON.stringify(component.data)));
        break;

      case 'spare':
        if (!updatedProject.spares) updatedProject.spares = [];
        updatedProject.spares.push(JSON.parse(JSON.stringify(component.data)));
        break;

      case 'labor':
        if (!updatedProject.labor) updatedProject.labor = [];
        updatedProject.labor.push(JSON.parse(JSON.stringify(component.data)));
        break;

      case 'failureModel':
        if (!updatedProject.failureModels) updatedProject.failureModels = [];
        updatedProject.failureModels.push(JSON.parse(JSON.stringify(component.data)));
        break;

      case 'weibullSet':
        if (!updatedProject.weibullSets) updatedProject.weibullSets = [];
        updatedProject.weibullSets.push(JSON.parse(JSON.stringify(component.data)));
        break;

      case 'bom':
        if (!updatedProject.boms) updatedProject.boms = [];
        updatedProject.boms.push(JSON.parse(JSON.stringify(component.data)));
        break;

      case 'taskGroup':
        if (!updatedProject.taskGroups) updatedProject.taskGroups = [];
        updatedProject.taskGroups.push(JSON.parse(JSON.stringify(component.data)));
        break;
    }

    // Track applied component
    if (!updatedProject.appliedComponents) {
      updatedProject.appliedComponents = [];
    }
    updatedProject.appliedComponents.push({
      libraryId: libraryId,
      componentId: componentId,
      componentName: component.name,
      appliedDate: new Date().toISOString()
    });

    return updatedProject;
  }

  /**
   * Save selected project entities to a library
   * @param {Object} projectData - Project data
   * @param {string} entityType - Entity type to save (e.g., 'equipment', 'spare')
   * @param {Array<string|number>} entityIds - IDs of entities to save
   * @param {string} libraryId - Target library ID
   * @returns {Array<Object>} Created components
   */
  saveToLibrary(projectData, entityType, entityIds, libraryId) {
    const library = this.libraries.get(libraryId);
    if (!library) {
      throw new Error(`Library not found: ${libraryId}`);
    }

    const validTypes = ['equipment', 'spare', 'labor', 'failureModel', 'weibullSet', 'bom', 'taskGroup'];
    if (!validTypes.includes(entityType)) {
      throw new Error(`Invalid entity type: ${entityType}`);
    }

    const entities = projectData[`${entityType}s`] || projectData[entityType] || [];
    const createdComponents = [];

    entityIds.forEach(entityId => {
      const entity = entities.find(e => e.id === entityId || e.name === entityId);

      if (entity) {
        const component = this.addComponent(libraryId, {
          type: entityType,
          name: entity.name || `${entityType}_${entityId}`,
          description: entity.description || `Saved from project at ${new Date().toISOString()}`,
          data: JSON.parse(JSON.stringify(entity)),
          tags: [entityType, 'saved-from-project']
        });

        createdComponents.push(component);
      }
    });

    return createdComponents;
  }

  // ==================== BUILT-IN LIBRARIES ====================

  /**
   * Initialize built-in libraries (WeibullDemo and SparesLaborExample equivalents)
   * @private
   */
  _initializeBuiltInLibraries() {
    // Built-in Weibull library with common component parameters
    this.libraries.set('lib_weibull_demo', {
      id: 'lib_weibull_demo',
      name: 'Weibull Demo',
      description: 'Built-in library with common Weibull parameters for standard equipment',
      author: 'OptimalAvailability',
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      version: '1.0.0',
      builtIn: true,
      components: [
        {
          id: 'comp_pump_standard',
          type: 'weibullSet',
          name: 'Centrifugal Pump - Standard',
          description: 'Typical centrifugal pump Weibull parameters',
          tags: ['equipment', 'pump', 'rotation'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            componentType: 'pump',
            reliability: 0.85,
            mttf: 8760, // 1 year in hours
            weibull: {
              shape: 1.8,
              scale: 10000,
              location: 0
            }
          }
        },
        {
          id: 'comp_motor_electric',
          type: 'weibullSet',
          name: 'Electric Motor',
          description: 'Standard electric motor failure distribution',
          tags: ['equipment', 'motor', 'electric'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            componentType: 'motor',
            reliability: 0.92,
            mttf: 12000,
            weibull: {
              shape: 2.1,
              scale: 15000,
              location: 0
            }
          }
        },
        {
          id: 'comp_valve_ball',
          type: 'weibullSet',
          name: 'Ball Valve',
          description: 'Standard ball valve reliability parameters',
          tags: ['equipment', 'valve', 'flow-control'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            componentType: 'valve',
            reliability: 0.95,
            mttf: 15000,
            weibull: {
              shape: 1.5,
              scale: 18000,
              location: 0
            }
          }
        },
        {
          id: 'comp_bearing_ball',
          type: 'weibullSet',
          name: 'Ball Bearing',
          description: 'Standard ball bearing Weibull distribution',
          tags: ['equipment', 'bearing', 'rotational'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            componentType: 'bearing',
            reliability: 0.88,
            mttf: 10000,
            weibull: {
              shape: 2.3,
              scale: 12000,
              location: 0
            }
          }
        },
        {
          id: 'comp_seal_mechanical',
          type: 'weibullSet',
          name: 'Mechanical Seal',
          description: 'Mechanical seal failure distribution',
          tags: ['equipment', 'seal', 'leakage-prevention'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            componentType: 'seal',
            reliability: 0.80,
            mttf: 7200,
            weibull: {
              shape: 1.6,
              scale: 8500,
              location: 0
            }
          }
        },
        {
          id: 'comp_gearbox_standard',
          type: 'weibullSet',
          name: 'Gearbox',
          description: 'Standard industrial gearbox reliability',
          tags: ['equipment', 'gearbox', 'power-transmission'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            componentType: 'gearbox',
            reliability: 0.90,
            mttf: 15000,
            weibull: {
              shape: 1.9,
              scale: 17000,
              location: 0
            }
          }
        }
      ]
    });

    // Built-in Spares and Labor library
    this.libraries.set('lib_spares_labor_example', {
      id: 'lib_spares_labor_example',
      name: 'Spares and Labor Example',
      description: 'Built-in library with example spare parts and labor rates',
      author: 'OptimalAvailability',
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
      version: '1.0.0',
      builtIn: true,
      components: [
        {
          id: 'comp_spare_gasket',
          type: 'spare',
          name: 'Gasket Kit',
          description: 'Standard gasket kit for pump maintenance',
          tags: ['spare', 'pump', 'seal'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            partNumber: 'GSK-001',
            description: 'Gasket Kit',
            unitCost: 125.00,
            leadTime: 2, // days
            stocked: true,
            quantity: 50
          }
        },
        {
          id: 'comp_spare_bearing',
          type: 'spare',
          name: 'Replacement Bearing',
          description: 'Ball bearing replacement assembly',
          tags: ['spare', 'bearing', 'rotation'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            partNumber: 'BRG-024',
            description: 'Ball Bearing Assembly',
            unitCost: 450.00,
            leadTime: 1,
            stocked: true,
            quantity: 20
          }
        },
        {
          id: 'comp_spare_filter',
          type: 'spare',
          name: 'Oil Filter',
          description: 'Hydraulic oil filter cartridge',
          tags: ['spare', 'filter', 'maintenance'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            partNumber: 'FLT-098',
            description: 'Oil Filter',
            unitCost: 85.50,
            leadTime: 1,
            stocked: true,
            quantity: 100
          }
        },
        {
          id: 'comp_labor_maintenance',
          type: 'labor',
          name: 'Maintenance Technician',
          description: 'Standard maintenance technician hourly rate',
          tags: ['labor', 'maintenance', 'technician'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            role: 'Maintenance Technician',
            hourlyRate: 65.00,
            availability: 'standard',
            skillLevel: 'intermediate'
          }
        },
        {
          id: 'comp_labor_specialist',
          type: 'labor',
          name: 'Specialist Engineer',
          description: 'Senior specialist engineer rate',
          tags: ['labor', 'engineering', 'specialist'],
          created: new Date().toISOString(),
          modified: new Date().toISOString(),
          data: {
            role: 'Specialist Engineer',
            hourlyRate: 125.00,
            availability: 'on-call',
            skillLevel: 'advanced'
          }
        }
      ]
    });
  }

  /**
   * Get built-in Weibull library
   * @returns {Object} Weibull demo library
   */
  getBuiltInWeibullLibrary() {
    return this.libraries.get('lib_weibull_demo');
  }

  /**
   * Get built-in Spares and Labor library
   * @returns {Object} Spares and labor example library
   */
  getBuiltInSparesLaborLibrary() {
    return this.libraries.get('lib_spares_labor_example');
  }

  /**
   * Get all built-in libraries
   * @returns {Array<Object>} Array of built-in libraries
   */
  getBuiltInLibraries() {
    return Array.from(this.libraries.values())
      .filter(lib => lib.builtIn)
      .map(lib => ({
        id: lib.id,
        name: lib.name,
        description: lib.description,
        componentCount: lib.components.length,
        builtIn: true
      }));
  }

  // ==================== STATIC CONSTANTS ====================

  /**
   * Static constant: Built-in Weibull library data
   * @static
   */
  static BUILTIN_WEIBULL_LIBRARY = {
    id: 'lib_weibull_demo',
    name: 'Weibull Demo',
    components: [
      { name: 'Centrifugal Pump', shape: 1.8, scale: 10000 },
      { name: 'Electric Motor', shape: 2.1, scale: 15000 },
      { name: 'Ball Valve', shape: 1.5, scale: 18000 },
      { name: 'Ball Bearing', shape: 2.3, scale: 12000 },
      { name: 'Mechanical Seal', shape: 1.6, scale: 8500 },
      { name: 'Gearbox', shape: 1.9, scale: 17000 }
    ]
  };

  /**
   * Static constant: Built-in Spares and Labor library data
   * @static
   */
  static BUILTIN_SPARES_LABOR_LIBRARY = {
    id: 'lib_spares_labor_example',
    name: 'Spares and Labor Example',
    spares: [
      { name: 'Gasket Kit', cost: 125.00, leadTime: 2 },
      { name: 'Replacement Bearing', cost: 450.00, leadTime: 1 },
      { name: 'Oil Filter', cost: 85.50, leadTime: 1 }
    ],
    labor: [
      { role: 'Maintenance Technician', hourlyRate: 65.00 },
      { role: 'Specialist Engineer', hourlyRate: 125.00 }
    ]
  };
}

export default LibraryManager;
